import React, { useCallback, useState } from 'react';
import { observer } from 'mobx-react';

import { FolderType } from 'stores/Folders/types';

import FolderTreeSelectItem from './FolderTreeSelectItem';
import {
  StyledDefaultFolderIcon,
  FolderItem,
  FolderItemContent,
  FolderName,
  StyledSelectedFolderIcon,
  TreeContainer,
} from './styles';

interface FolderTreePickerProps {
  folders: FolderType[];
  selectedFolderId: string | null;
  onSelect: (folderId: string | null) => void;
  allowNone?: boolean;
  excludedFolderIds?: Set<string>;
}

const findAncestorIds = (folders: FolderType[], targetId: string, trail: string[] = []): string[] | null => {
  for (const folder of folders) {
    if (folder.uuid === targetId) return trail;
    const found = findAncestorIds(folder.subfolders ?? [], targetId, [...trail, folder.uuid]);
    if (found) return found;
  }
  return null;
};

const pruneFolders = (folders: FolderType[], excluded: Set<string>): FolderType[] =>
  folders
    .filter((folder) => !excluded.has(folder.uuid))
    .map((folder) => ({ ...folder, subfolders: pruneFolders(folder.subfolders ?? [], excluded) }));

const FolderTreePicker: React.FC<FolderTreePickerProps> = ({
  folders,
  selectedFolderId,
  onSelect,
  allowNone = false,
  excludedFolderIds,
}) => {
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(
    () => new Set(selectedFolderId ? (findAncestorIds(folders, selectedFolderId) ?? []) : [])
  );

  const handleToggleExpand = useCallback((folderId: string): void => {
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const visibleFolders = excludedFolderIds?.size ? pruneFolders(folders, excludedFolderIds) : folders;
  const isNoneSelected = selectedFolderId === null;

  return (
    <TreeContainer>
      {allowNone && (
        <FolderItem $depth={0} onClick={() => onSelect(null)}>
          <FolderItemContent $selected={isNoneSelected}>
            {isNoneSelected ? (
              <StyledSelectedFolderIcon fontSize="small" />
            ) : (
              <StyledDefaultFolderIcon fontSize="small" />
            )}
            <FolderName $selected={isNoneSelected}>None (top level)</FolderName>
          </FolderItemContent>
        </FolderItem>
      )}
      {visibleFolders.map((folder) => (
        <FolderTreeSelectItem
          key={folder.uuid}
          folder={folder}
          depth={0}
          selectedFolderId={selectedFolderId}
          expandedFolderIds={expandedFolderIds}
          onSelect={onSelect}
          onToggleExpand={handleToggleExpand}
        />
      ))}
    </TreeContainer>
  );
};

export default observer(FolderTreePicker);
